/**
 * Redux slice du contexte d'une citation (chantier C23 « Confrontation », SP5
 * « Voir le contexte », spec § 7.1).
 *
 * L'état est indexé par **note** (`byNote[noteId]`) : le droit tient à la note et
 * le texte cherché est le sien. Une garde `requestId` écarte la réponse périmée
 * d'une même note. Un 404 est un état (« indisponible », sans raison : la note
 * n'est plus lisible), pas une erreur ; une réponse `statut: 'indisponible'` est
 * `ready` avec son DTO — c'est le composant qui en lit la raison. Le composant relit
 * à chaque dépli (la citation a pu changer par « Modifier le passage ») ; le slice
 * garde la dernière lecture. Aucune notification globale.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { noteService } from '../../services/noteAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import type { ContexteCitationDto } from '../../types/api';

export type ContexteStatus = 'idle' | 'loading' | 'ready' | 'indisponible' | 'error';

export interface ContexteEntry {
    status: ContexteStatus;
    contexte?: ContexteCitationDto;
    /** Dernier chargement lancé pour cette note : garde anti-course. */
    requestId: string | null;
}

export interface ContexteCitationState {
    byNote: Record<string, ContexteEntry>;
}

export const initialContexteCitationState: ContexteCitationState = { byNote: {} };

/** Entrée de repli des sélecteurs : référence stable, sinon re-rendus en boucle. */
const EMPTY_ENTRY: ContexteEntry = Object.freeze({ status: 'idle', requestId: null }) as ContexteEntry;

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}

export const fetchContexteCitation = createAsyncThunk<
    ContexteCitationDto,
    { noteId: string; instance: any },
    { rejectValue: 'indisponible' | 'error' }
>('contexteCitation/fetch', async ({ noteId, instance }, { rejectWithValue }) => {
    try {
        return await noteService.getContexteCitation(noteId, await tokenOf(instance));
    } catch (error: any) {
        return rejectWithValue(error?.response?.status === 404 ? 'indisponible' : 'error');
    }
});

const contexteCitationSlice = createSlice({
    name: 'contexteCitation',
    initialState: initialContexteCitationState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchContexteCitation.pending, (state, action) => {
                const { noteId } = action.meta.arg;
                const previous = state.byNote[noteId];
                state.byNote[noteId] = { status: 'loading', contexte: previous?.contexte, requestId: action.meta.requestId };
            })
            .addCase(fetchContexteCitation.fulfilled, (state, action) => {
                const { noteId } = action.meta.arg;
                const entry = state.byNote[noteId];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byNote[noteId] = { status: 'ready', contexte: action.payload, requestId: action.meta.requestId };
            })
            .addCase(fetchContexteCitation.rejected, (state, action) => {
                const { noteId } = action.meta.arg;
                const entry = state.byNote[noteId];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                state.byNote[noteId] = { status: action.payload === 'indisponible' ? 'indisponible' : 'error', requestId: action.meta.requestId };
            });
    },
});

export default contexteCitationSlice.reducer;

// --- Sélecteurs (tolérants : un store partiel rend l'entrée vide) ---
export const selectContexte = (noteId: string) => (state: any): ContexteEntry =>
    state?.contexteCitation?.byNote?.[noteId] ?? EMPTY_ENTRY;
